import { Cloud, Clock, MapPin, ShieldCheck, HeartHandshake } from "lucide-react";
import SituationPage from "@/components/site/SituationPage";
import { useMeta } from "@/hooks/useMeta";

/* A "sealed timeline" mock, different from the other pages' mini-demos */
function SealedTimelineMock() {
  const entries = [
    { time: "9:42 PM", label: "Recording started", place: "Home · 33.3062° N" },
    { time: "9:58 PM", label: "Location update sealed", place: "In transit · I-10 W" },
    { time: "10:11 PM", label: "Recording still active", place: "Rest stop · 33.4152° N" },
  ];
  return (
    <div className="mx-auto lg:mx-0 max-w-sm rounded-2xl border border-[color:var(--yn-border)] bg-white p-5 sm:p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <Clock size={16} style={{ color: "var(--yn-primary)" }} />
        <span className="text-xs font-semibold uppercase tracking-wider text-[color:var(--yn-primary)]">
          Sealed timeline
        </span>
      </div>

      <ol className="mt-5 relative border-l border-[color:var(--yn-border)] pl-6 space-y-6">
        {entries.map((e, i) => (
          <li key={i} className="relative">
            <span
              className="absolute -left-[27px] top-1.5 inline-flex h-3 w-3 rounded-full ring-4 ring-white"
              style={{ background: "var(--yn-primary)" }}
              aria-hidden="true"
            />
            <p className="text-sm font-semibold text-[color:var(--yn-ink)]">
              {e.time}
            </p>
            <p className="mt-0.5 text-sm text-[color:var(--yn-ink)]/85">
              {e.label}
            </p>
            <p className="mt-0.5 text-xs text-[color:var(--yn-muted)]">
              {e.place}
            </p>
          </li>
        ))}
      </ol>

      <div className="mt-5 pt-4 border-t border-[color:var(--yn-border)] flex items-center gap-2 text-[11px] text-[color:var(--yn-muted)]">
        <Cloud size={13} />
        Already uploaded, safe if the phone is lost.
      </div>
    </div>
  );
}

export default function Missing() {
  useMeta({
    title: "Personal safety recording app, YeboNadi",
    description:
      "For people who travel, meet strangers, work late, or live alone. If you can't tell the story, your record still can. YeboNadi seals audio, timestamps, and locations to encrypted cloud storage, so the record survives the phone.",
    path: "/missing",
  });

  return (
    <SituationPage
      slug="missing"
      kicker="If something ever happens to you"
      headline="If you can't tell the story, your record still can."
      narrative={[
        "This is the moment we hope no one ever has. You travel. You meet strangers for work or for coffee. You walk home late. You live alone. Most of the time, nothing happens. Sometimes, something does.",
        "If you cannot tell someone what happened, because you are hurt, unreachable, or missing, the people who love you are left trying to piece together a timeline from other people's memories. They call, they ask, they wait. That is a very hard place to start from.",
        "What your phone captured and already uploaded is a different kind of starting point. A sealed timeline. A last known location. Audio from the moments before things went wrong. It does not undo what happened. It gives the people trying to find you somewhere real to begin.",
      ]}
      miniDemo={<SealedTimelineMock />}
      coversHeading="What survives"
      covers={[
        {
          icon: Cloud,
          title: "Batched encrypted uploads",
          body: "As you record, YeboNadi batch-uploads to encrypted cloud storage. Even if your phone is later lost, damaged, or taken, the sealed record is already off the device.",
        },
        {
          icon: Clock,
          title: "Sealed timeline",
          body: "Every capture carries an embedded timestamp, sealed with the recording itself. The order of events is preserved and verifiable.",
        },
        {
          icon: MapPin,
          title: "Location, embedded in the record",
          body: "GPS is written into each sealed recording at capture, so the last confirmed location is part of the audit trail, not just a phone log.",
        },
        {
          icon: ShieldCheck,
          title: "Released only by legal order or signed release",
          body: "Nobody, including you, can play a sealed recording inside the app. If a loved one needs access as part of an investigation, an authorized party, the police, your representative, or the court, submits a legal order or a signed release along with your encryption/decryption key. On approval, YeboNadi decrypts the record and delivers it via secure cloud storage. A retrieval fee applies.",
        },
      ]}
      subsectionTitle="Set it up once. Tell one person you have it."
      subsectionBody={
        <>
          <p>
            You should not have to think about YeboNadi every day. The point is
            to install it before you need it, learn the one tap that starts a
            recording, and then let it live quietly on your home screen.
          </p>
          <p>
            The other thing that matters is this: tell one or many very
            trusted persons that you have it. A partner, a parent, a friend,
            a sibling. Someone who would know to mention it if you did not
            come home. And share your encryption/decryption key with that
            one or many very trusted persons, without it, no one can
            retrieve what you sealed, even under a legal order. They do not
            need any login. They just need to know the app exists, and hold
            your key, so it becomes part of the picture instead of an
            unopened icon.
          </p>
          <p className="flex items-center gap-2 text-[color:var(--yn-ink)] font-medium">
            <HeartHandshake size={18} style={{ color: "var(--yn-primary)" }} />
            Set it up once. Tell one person you have it.
          </p>
        </>
      }
      footerNote={
        <>
          If you or someone you love is in immediate danger, please contact
          local emergency services first. In the US call 911. If you need to
          talk to someone, the 988 Suicide &amp; Crisis Lifeline is available
          by call or text.
        </>
      }
    />
  );
}
